import { useConfig } from 'hooks/use-config';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import { useQueryClient } from '@tanstack/react-query';
import * as customGptService from '../services/custom-gpt-service';
import { CreateCustomGptRequest, UpdateCustomGptRequest } from '../types/custom-gpt.types';

export const useCreateCustomGpt = () => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationFn: (payload: CreateCustomGptRequest) => 
            customGptService.createCustomGpt(configurationSettings, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-gpts'] });
        },
    });
};

export const useUploadCustomGptFile = () => {
    const { configurationSettings } = useConfig();
    return useGlobalMutation({
        mutationFn: ({ customGptId, file }: { customGptId: string; file: File }) => 
            customGptService.uploadFileToCustomGpt(configurationSettings, customGptId, file),
    });
};

export const useDeleteCustomGptFile = () => {
    const { configurationSettings } = useConfig();
    return useGlobalMutation({
        mutationFn: ({ customGptId, fileId }: { customGptId: string; fileId: string }) => 
            customGptService.deleteCustomGptFile(configurationSettings, customGptId, fileId),
    });
};

export const useDeleteCustomGpt = () => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();
    return useGlobalMutation({
        mutationFn: (customGptId: string) => 
            customGptService.deleteCustomGpt(configurationSettings, customGptId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['custom-gpts'] });
        },
    });
};

export const useListCustomGpts = () => {
    const { configurationSettings } = useConfig();
    return useGlobalQuery({
        queryKey: ['custom-gpts'],
        queryFn: () => customGptService.listCustomGpts(configurationSettings),
        enabled: !!configurationSettings,
    });
};

export const useGetCustomGpt = (customGptId: string) => {
    const { configurationSettings } = useConfig();
    return useGlobalQuery({
        queryKey: ['custom-gpt', customGptId],
        queryFn: () => customGptService.getCustomGpt(configurationSettings, customGptId),
        enabled: !!configurationSettings && !!customGptId,
    });
};

export const useUpdateCustomGpt = () => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationFn: ({ customGptId, payload }: { customGptId: string; payload: UpdateCustomGptRequest }) =>
            customGptService.updateCustomGpt(configurationSettings, customGptId, payload),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['custom-gpts'] });
            queryClient.invalidateQueries({ queryKey: ['custom-gpt', variables.customGptId] });
            queryClient.invalidateQueries({ queryKey: ['custom-gpt-files', variables.customGptId] });
        },
    });
};
export const useListCustomGptFiles = (customGptId: string) => {
    const { configurationSettings } = useConfig();
    return useGlobalQuery({
        queryKey: ['custom-gpt-files', customGptId],
        queryFn: () => customGptService.listFilesForCustomGpt(configurationSettings, customGptId),
        enabled: !!configurationSettings && !!customGptId,
    });
};
